import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './Home.css';
import { useNavigate } from 'react-router-dom';
import { db } from '../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTruckMedical, 
  faSink, 
  faPlug, 
  faScrewdriverWrench 
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import { BiSolidAmbulance } from 'react-icons/bi';
import { FaWrench, FaPlug } from 'react-icons/fa';
import { MdPlumbing } from 'react-icons/md';
import InstallPWA from '../components/InstallPWA';

const Home = () => {
  const navigate = useNavigate();
  const [advertisements, setAdvertisements] = useState([]);

  useEffect(() => {
    const loadAdvertisements = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        console.log('Today:', today);

        const adsRef = collection(db, 'advertisements');
        const adsSnapshot = await getDocs(adsRef);
        
        if (adsSnapshot.empty) {
          console.log('No advertisements found in database');
          return;
        }

        const activeAds = adsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(ad => {
            console.log('Checking ad:', {
              id: ad.id,
              startDate: ad.startDate,
              endDate: ad.endDate,
              isActive: ad.startDate <= today && ad.endDate >= today
            });
            
            return ad.startDate <= today && ad.endDate >= today;
          });

        console.log('Active ads:', activeAds);
        setAdvertisements(activeAds);
      } catch (error) {
        console.error('Error loading advertisements:', error);
      }
    };

    loadAdvertisements();
  }, []);

  const handleFindServices = () => {
    navigate('/providers');
  };

  // First, let's create a mapping for the categories
  const categoryMapping = {
    'Plumbing': 'Plumber',
    'Electrical': 'Electrician',
    'Handy Man': 'Handyman'
  };

  // Update the handleServiceClick function
  const handleServiceClick = (service) => {
    // Use the mapping if it exists, otherwise use the original service name
    const mappedCategory = categoryMapping[service] || service;
    console.log('Mapped category:', mappedCategory);
    navigate('/providers', { 
      state: { selectedCategory: mappedCategory }
    });
  };

  // For the icons, we can use custom SVG icons with colors
  const services = [
    { 
      name: 'Emergency Services', 
      icon: <BiSolidAmbulance 
        size={30} 
        color="#FF4444"
        className="emergency-icon" // We'll add animation via CSS
      />,
      description: '24/7 emergency assistance' 
    },
    { 
      name: 'Plumbing', 
      icon: <MdPlumbing 
        size={30} 
        color="#2196F3"
      />,
      description: 'Professional plumbing services' 
    },
    { 
      name: 'Electrical', 
      icon: <FaPlug 
        size={30} 
        color="#FFC107"
      />,
      description: 'Professional electrical services' 
    },
    { 
      name: 'Handy Man', 
      icon: <FaWrench 
        size={30} 
        color="#757575"
      />,
      description: 'Professional handyman services' 
    }
];


  // Update the Advertisement Section
  const AdSection = () => {
    const [currentAdIndex, setCurrentAdIndex] = useState(0);
    const [loadedImages, setLoadedImages] = useState({});
    const [validAds, setValidAds] = useState([]);

    useEffect(() => {
      // Filter out expired ads
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
      
      const activeAds = advertisements.filter(ad => {
        const endDate = new Date(ad.endDate);
        endDate.setHours(23, 59, 59, 999); // Set to end of day
        
        const startDate = new Date(ad.startDate);
        startDate.setHours(0, 0, 0, 0);
        
        console.log('Ad date check:', {
          id: ad.id,
          title: ad.title,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          today: today.toISOString(),
          isActive: startDate <= today && today <= endDate
        });
        
        return startDate <= today && today <= endDate;
      });

      setValidAds(activeAds);
      console.log('Active ads:', activeAds.length);
    }, [advertisements]);

    useEffect(() => {
      if (validAds.length > 1) {
        const interval = setInterval(() => {
          setCurrentAdIndex((prevIndex) => 
            prevIndex === validAds.length - 1 ? 0 : prevIndex + 1
          );
        }, 7000);

        return () => clearInterval(interval);
      }
    }, [validAds.length]);

    // Only show section if there are valid ads
    if (!validAds.length) {
      return null;
    }

    return (
      <section className="ad-section">
        <div className="ad-banner">
          <div 
            className="ad-scroll"
            style={{
              transform: `translateX(-${currentAdIndex * 100}%)`
            }}
          >
            {validAds.map((ad) => (
              <motion.div
                key={ad.id}
                className="ad-banner-item"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="ad-label">Ad</div>
                <div className="ad-label-desktop">Advertisement</div>
                <img 
                  src={ad.imageUrl}
                  alt={ad.title || 'Advertisement'}
                  className={loadedImages[ad.id] ? 'loaded' : ''}
                  onLoad={() => setLoadedImages(prev => ({ ...prev, [ad.id]: true }))}
                />
                {ad.link && (
                  <button 
                    className="visit-website-btn"
                    onClick={() => window.open(ad.link, '_blank')}
                  >
                    Visit Website
                  </button>
                )}
              </motion.div>
            ))}
          </div>
          
          {validAds.length > 1 && (
            <div className="ad-nav-dots">
              {validAds.map((_, index) => (
                <button
                  key={index}
                  className={`nav-dot ${index === currentAdIndex ? 'active' : ''}`}
                  onClick={() => setCurrentAdIndex(index)}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    );
  };

  // Add this useEffect to scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home">
      {/* Hero Section */}
      <motion.div 
        className="hero-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            AroundU
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            "Building stronger communities, one service at a time."
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            Connect with qualified professionals in your area
          </motion.p>
          <motion.button
            className="cta-button"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleFindServices}
          >
            Find Services
          </motion.button>
        </div>
      </motion.div>

      {/* Featured Services Section */}
      <section className="featured-services">
        <h2>Popular Services</h2>
        <div className="services-grid">
          {services.map((service, index) => (
            <motion.div
              key={service.name}
              className="service-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 + 0.8 }}
              whileHover={{ y: -5, boxShadow: '0 5px 15px rgba(0,0,0,0.2)' }}
              onClick={() => handleServiceClick(service.name)}
              style={{ cursor: 'pointer' }}
            >
              <div className="service-icon">
                {service.icon}
              </div>
              <h3>{service.name}</h3>
              <p>{service.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Advertisement Section */}
      <AdSection />

      {/* Why Choose Us Section */}
      <motion.section 
        className="why-choose-us"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2>Why Choose Our Platform</h2>
        <div className="features-grid">
          {[
            { 
              title: 'Verified Professionals', 
              icon: '✓', 
              description: 'All service providers are thoroughly vetted' 
            },
            { 
              title: 'Rate & Review', 
              icon: '⭐', 
              description: 'Rate your fellow service providers and grow the community together' 
            },
            { 
              title: '24/7 Support', 
              icon: '💬', 
              description: 'Round-the-clock customer support' 
            },
            { 
              title: 'Convenience Guaranteed', 
              icon: '✅', 
              description: 'Easiest way to find services' 
            }
          ].map((feature, index) => (
            <motion.div
              key={feature.title}
              className="feature-card"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Replace the footer section with the Footer component */}
      <Footer />

      <InstallPWA />
    </div>
  );
};

export default Home;